import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, LogOut, Upload, Trash2, Camera, ImageIcon, Star, Save, MessageSquareQuote, Check, Sparkles } from "lucide-react";
import { API_URL } from "@/lib/site";
import { fileToBase64 } from "@/lib/upload";
import { Reveal } from "@/components/motion";
import { useSEO } from "@/components/SEO";

const CATEGORIES = [
  { id: "auto", label: "Auto" },
  { id: "moto", label: "Moto" },
  { id: "scooter", label: "Scooter" },
  { id: "atelier", label: "Atelier" },
];

const ROLES = [
  { id: "gallery", label: "Galerie" },
  { id: "before", label: "Avant" },
  { id: "after", label: "Après" },
  { id: "portrait", label: "Portrait (page À propos)" },
];

const ROLE_BADGE = { gallery: "Galerie", before: "Avant", after: "Après", portrait: "Portrait" };

export default function Admin() {
  useSEO("Espace atelier | BM MECHANICS", "Espace réservé à l'atelier BM MECHANICS.");
  const [token, setToken] = useState(() => localStorage.getItem("bm_admin_token"));
  const [auth, setAuth] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [caption, setCaption] = useState("");
  const [category, setCategory] = useState("atelier");
  const [role, setRole] = useState("gallery");
  const [pairId, setPairId] = useState("");
  const [avis, setAvis] = useState([]);
  const [settings, setSettings] = useState({ google_review_url: "", video_url: "", bio_text: "", promo_text: "" });
  const [avisForm, setAvisForm] = useState({ auteur: "", texte: "", note: 5, source: "Google" });
  const [aiBusy, setAiBusy] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const load = async () => {
    try {
      const r = await fetch(`${API_URL}/photos`);
      setPhotos(await r.json());
      const c = await fetch(`${API_URL}/contenu`);
      const data = await c.json();
      setSettings({ google_review_url: "", video_url: "", bio_text: "", promo_text: "", ...(data.settings || {}) });
      if (token) {
        const av = await fetch(`${API_URL}/admin/avis`, { headers });
        if (av.ok) setAvis(await av.json());
      }
    } catch { /* silencieux */ }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!token) { setAuth(false); return; }
    fetch(`${API_URL}/auth/me`, { headers })
      .then((r) => setAuth(r.ok))
      .catch(() => setAuth(false));
  }, [token]);

  const login = async (e) => {
    e.preventDefault();
    setLoginError("");
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Identifiants incorrects");
      }
      const d = await r.json();
      localStorage.setItem("bm_admin_token", d.token);
      setToken(d.token);
      toast.success("Connecté à l'espace atelier");
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("bm_admin_token");
    setToken(null);
    setAuth(false);
  };

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    let ok = 0;
    try {
      for (const f of files) {
        const data = await fileToBase64(f);
        const r = await fetch(`${API_URL}/admin/photos`, {
          method: "POST",
          headers: { ...headers, "Content-Type": "application/json" },
          body: JSON.stringify({
            data,
            caption,
            category,
            role,
            pair_id: role === "after" && pairId ? pairId : null,
          }),
        });
        if (!r.ok) throw new Error("upload");
        ok += 1;
      }
      toast.success(`${ok} photo(s) publiée(s) sur le site`);
      setCaption("");
      await load();
    } catch {
      toast.error("Échec de l'envoi — vérifiez votre connexion et réessayez");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    try {
      await fetch(`${API_URL}/admin/photos/${id}`, { method: "DELETE", headers });
      toast.success("Photo supprimée");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const approveAvis = async (id) => {
    try {
      const r = await fetch(`${API_URL}/admin/avis/${id}/publier`, { method: "PUT", headers });
      if (!r.ok) throw new Error();
      toast.success("Avis publié sur la page d'accueil");
      load();
    } catch {
      toast.error("Échec de la publication");
    }
  };

  const addAvis = async (e) => {
    e.preventDefault();
    if (!avisForm.auteur.trim() || !avisForm.texte.trim()) {
      toast.error("Nom et texte de l'avis requis");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/avis`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(avisForm),
      });
      if (!r.ok) throw new Error();
      toast.success("Avis publié sur la page d'accueil");
      setAvisForm({ auteur: "", texte: "", note: 5, source: "Google" });
      await load();
    } catch {
      toast.error("Échec de la publication");
    } finally {
      setBusy(false);
    }
  };

  const removeAvis = async (id) => {
    try {
      await fetch(`${API_URL}/admin/avis/${id}`, { method: "DELETE", headers });
      toast.success("Avis supprimé");
      load();
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const assist = async (kind, context, apply) => {
    setAiBusy(kind);
    try {
      const r = await fetch(`${API_URL}/admin/assist`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ kind, context }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      apply(d.text);
      toast.success("Proposition générée — modifiable avant enregistrement");
    } catch {
      toast.error("Assistant indisponible, réessayez");
    } finally {
      setAiBusy("");
    }
  };

  const saveReponse = async (id) => {
    try {
      const r = await fetch(`${API_URL}/admin/avis/${id}/reponse`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ reponse: replyDrafts[id] ?? "" }),
      });
      if (!r.ok) throw new Error();
      toast.success("Réponse publiée sous l'avis");
      setReplyDrafts((d) => {
        const c = { ...d };
        delete c[id];
        return c;
      });
      load();
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await fetch(`${API_URL}/admin/settings`, {
        method: "PUT",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!r.ok) throw new Error();
      toast.success("Réglages enregistrés — visibles immédiatement sur le site");
    } catch {
      toast.error("Échec de l'enregistrement");
    } finally {
      setBusy(false);
    }
  };

  const beforePhotos = photos.filter((p) => p.role === "before");

  return (
    <div data-testid="admin-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {auth === null && (
          <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#9A968C]">Vérification…</p>
        )}

        {auth === false && (
          <Reveal>
            <div className="bg-white border border-[#D8D4CA] p-6 sm:p-10 max-w-md mx-auto">
              <span className="inline-flex h-12 w-12 items-center justify-center bg-[#121212] text-[#8C7B5D]">
                <Lock className="h-5 w-5" />
              </span>
              <h1 className="mt-6 font-display text-2xl font-black uppercase tracking-tight text-[#121212]">
                Espace atelier
              </h1>
              <p className="mt-2 text-sm text-[#4A4D53]">
                Réservé à BM MECHANICS — publiez vos photos directement depuis votre téléphone.
              </p>
              <form onSubmit={login} data-testid="admin-login-form" className="mt-6 space-y-5">
                <div>
                  <label htmlFor="admin-email" className="bm-label">Email</label>
                  <input
                    id="admin-email"
                    data-testid="admin-email-input"
                    type="email"
                    className="bm-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="username"
                  />
                </div>
                <div>
                  <label htmlFor="admin-password" className="bm-label">Mot de passe</label>
                  <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    className="bm-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                  />
                </div>
                {loginError && (
                  <p data-testid="admin-login-error" className="text-xs text-[#A33E2B]">{loginError}</p>
                )}
                <button
                  type="submit"
                  data-testid="admin-login-btn"
                  disabled={busy}
                  className="w-full bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors disabled:opacity-50"
                >
                  {busy ? "Connexion…" : "Se connecter"}
                </button>
              </form>
            </div>
          </Reveal>
        )}

        {auth === true && (
          <>
            <div className="flex items-center justify-between gap-4 mb-8">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#8C7B5D]">Espace atelier</p>
                <h1 className="mt-2 font-display text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#121212]">
                  Photos du site
                </h1>
              </div>
              <button
                data-testid="admin-logout-btn"
                onClick={logout}
                className="inline-flex items-center gap-2 border border-[#D8D4CA] px-4 py-2.5 font-mono text-[11px] uppercase tracking-[0.16em] text-[#4A4D53] hover:border-[#121212] hover:text-[#121212] transition-colors"
              >
                <LogOut className="h-4 w-4" /> Quitter
              </button>
            </div>

            <div className="bg-white border border-[#D8D4CA] p-6 sm:p-8">
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#121212] flex items-center gap-3">
                <Camera className="h-5 w-5 text-[#8C7B5D]" /> Ajouter des photos
              </h2>
              <p className="mt-2 text-sm text-[#4A4D53]">
                Depuis votre téléphone : touchez « Choisir des photos », prenez une photo ou
                sélectionnez-la dans votre galerie. Elle apparaît immédiatement sur le site.
              </p>
              <p className="mt-2 text-sm text-[#4A4D53]">
                <span className="font-medium text-[#121212]">Beaucoup de photos pour une même intervention ?</span>{" "}
                Sélectionnez-les toutes ensemble avec la même légende (ex. « Detailing — Golf GTI ») :
                elles formeront un seul album dans la galerie, que le visiteur pourra feuilleter en grand.
              </p>
              <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="up-category" className="bm-label">Catégorie</label>
                  <select id="up-category" data-testid="admin-upload-category" className="bm-input" value={category} onChange={(e) => setCategory(e.target.value)}>
                    {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label htmlFor="up-role" className="bm-label">Emplacement</label>
                  <select id="up-role" data-testid="admin-upload-role" className="bm-input" value={role} onChange={(e) => setRole(e.target.value)}>
                    {ROLES.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                  </select>
                </div>
                {role === "after" && (
                  <div className="sm:col-span-2">
                    <label htmlFor="up-pair" className="bm-label">Photo « avant » correspondante</label>
                    <select id="up-pair" data-testid="admin-pair-select" className="bm-input" value={pairId} onChange={(e) => setPairId(e.target.value)}>
                      <option value="">Sélectionner la photo avant…</option>
                      {beforePhotos.map((p) => (
                        <option key={p.id} value={p.pair_id}>{p.caption || `Photo du ${new Date(p.created_at).toLocaleDateString("fr-CH")}`}</option>
                      ))}
                    </select>
                    {beforePhotos.length === 0 && (
                      <p className="mt-1.5 text-xs text-[#A33E2B]">Publiez d'abord une photo « Avant ».</p>
                    )}
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label htmlFor="up-caption" className="bm-label">Légende (facultatif)</label>
                  <input id="up-caption" data-testid="admin-caption-input" className="bm-input" placeholder="Ex. Freinage complet — Honda CB500F" value={caption} onChange={(e) => setCaption(e.target.value)} />
                  <button
                    type="button"
                    data-testid="admin-ai-caption-btn"
                    disabled={aiBusy !== ""}
                    onClick={() => assist("caption", `Catégorie : ${category}. Emplacement : ${ROLE_BADGE[role] || role}.`, (t) => setCaption(t))}
                    className="mt-2 inline-flex items-center gap-2 border border-[#D8D4CA] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#4A4D53] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> {aiBusy === "caption" ? "Génération…" : "Proposer une légende (IA)"}
                  </button>
                </div>
              </div>
              <label
                data-testid="admin-upload-zone"
                className="mt-6 flex flex-col items-center justify-center gap-2 border border-dashed border-[#B7B2A4] bg-[#F6F3EB] px-6 py-10 cursor-pointer hover:border-[#121212] transition-colors"
              >
                <Upload className="h-6 w-6 text-[#8C7B5D]" />
                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#5A5D64]">
                  {busy ? "Envoi en cours…" : "Choisir des photos (appareil photo ou galerie)"}
                </span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={onFiles}
                  disabled={busy || (role === "after" && !pairId && beforePhotos.length > 0)}
                  data-testid="admin-photo-input"
                />
              </label>
            </div>

            <div className="mt-8" data-testid="admin-photos-list">
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#121212] flex items-center gap-3 mb-5">
                <ImageIcon className="h-5 w-5 text-[#8C7B5D]" /> Photos publiées ({photos.length})
              </h2>
              {photos.length === 0 && (
                <p className="text-sm text-[#9A968C] border border-dashed border-[#D8D4CA] p-6 text-center">
                  Aucune photo pour l'instant — les photos d'illustration restent affichées sur le site.
                </p>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {photos.map((p, i) => (
                  <div key={p.id} data-testid={`admin-photo-${i}`} className="bg-white border border-[#D8D4CA] p-2">
                    <img src={p.data} alt={p.caption || "Photo atelier"} className="w-full aspect-square object-cover" loading="lazy" />
                    <div className="flex items-center justify-between gap-2 pt-2 px-1">
                      <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-[#8C7B5D]">
                        {ROLE_BADGE[p.role] || p.role} · {p.category}
                      </span>
                      <button
                        data-testid={`admin-photo-delete-${i}`}
                        aria-label="Supprimer cette photo"
                        onClick={() => remove(p.id)}
                        className="text-[#9A968C] hover:text-[#A33E2B] transition-colors"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {p.caption && <p className="px-1 pb-1 text-xs text-[#4A4D53] truncate">{p.caption}</p>}
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-8 bg-white border border-[#D8D4CA] p-6 sm:p-8" data-testid="admin-avis-section">
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#121212] flex items-center gap-3">
                <MessageSquareQuote className="h-5 w-5 text-[#8C7B5D]" /> Avis clients
              </h2>
              <p className="mt-2 text-sm text-[#4A4D53]">
                Collez ici vos vrais avis (Google, Facebook, bouche-à-oreille). Dès le premier avis
                publié, il remplace les emplacements réservés de la page d'accueil.
              </p>
              <form onSubmit={addAvis} data-testid="admin-avis-form" className="mt-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label htmlFor="avis-auteur" className="bm-label">Nom / initiale *</label>
                    <input id="avis-auteur" data-testid="admin-avis-auteur" className="bm-input" placeholder="Ex. Julien M." value={avisForm.auteur} onChange={(e) => setAvisForm((f) => ({ ...f, auteur: e.target.value }))} />
                  </div>
                  <div>
                    <label htmlFor="avis-note" className="bm-label">Note</label>
                    <select id="avis-note" data-testid="admin-avis-note" className="bm-input" value={avisForm.note} onChange={(e) => setAvisForm((f) => ({ ...f, note: Number(e.target.value) }))}>
                      {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} / 5</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="avis-source" className="bm-label">Source</label>
                    <select id="avis-source" data-testid="admin-avis-source" className="bm-input" value={avisForm.source} onChange={(e) => setAvisForm((f) => ({ ...f, source: e.target.value }))}>
                      {["Google", "Facebook", "Bouche-à-oreille"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label htmlFor="avis-texte" className="bm-label">Texte de l'avis *</label>
                  <textarea id="avis-texte" data-testid="admin-avis-texte" className="bm-input min-h-[90px] resize-y" placeholder="Collez le texte exact de l'avis…" value={avisForm.texte} onChange={(e) => setAvisForm((f) => ({ ...f, texte: e.target.value }))} />
                </div>
                <button type="submit" data-testid="admin-avis-submit" disabled={busy} className="w-full sm:w-auto bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors disabled:opacity-50">
                  Publier l'avis
                </button>
              </form>
              {avis.filter((a) => a.status === "pending").length > 0 && (
                <div className="mt-6" data-testid="admin-avis-pending">
                  <p className="bm-label">Avis déposés par les visiteurs — à valider</p>
                  <div className="space-y-3">
                    {avis.filter((a) => a.status === "pending").map((a, i) => (
                      <div key={a.id} data-testid={`admin-avis-pending-${i}`} className="border border-[#8C7B5D]/50 bg-[#F6F3EB] p-4">
                        <div className="flex gap-0.5 text-[#8C7B5D]">
                          {[...Array(a.note)].map((_, s) => <Star key={s} className="h-3.5 w-3.5 fill-current" />)}
                        </div>
                        <p className="mt-2 text-sm text-[#4A4D53]">« {a.texte} »</p>
                        <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#9A968C]">{a.auteur} · Site web</p>
                        <div className="mt-3 flex gap-2">
                          <button
                            data-testid={`admin-avis-approve-${i}`}
                            onClick={() => approveAvis(a.id)}
                            className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] font-mono text-[10px] uppercase tracking-[0.16em] px-4 py-2.5 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
                          >
                            <Check className="h-3.5 w-3.5" /> Publier
                          </button>
                          <button
                            data-testid={`admin-avis-reject-${i}`}
                            onClick={() => removeAvis(a.id)}
                            className="inline-flex items-center gap-2 border border-[#D8D4CA] text-[#4A4D53] font-mono text-[10px] uppercase tracking-[0.16em] px-4 py-2.5 hover:border-[#A33E2B] hover:text-[#A33E2B] transition-colors"
                          >
                            <Trash2 className="h-3.5 w-3.5" /> Supprimer
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {avis.filter((a) => a.status === "published").length > 0 && (
                <div className="mt-6 space-y-3" data-testid="admin-avis-list">
                  <p className="bm-label">Avis publiés sur la page d'accueil</p>
                  {avis.filter((a) => a.status === "published").map((a, i) => (
                    <div key={a.id} data-testid={`admin-avis-item-${i}`} className="border border-[#EFECE4] p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex gap-0.5 text-[#8C7B5D]">
                            {[...Array(a.note)].map((_, s) => <Star key={s} className="h-3.5 w-3.5 fill-current" />)}
                          </div>
                          <p className="mt-2 text-sm text-[#4A4D53]">« {a.texte} »</p>
                          <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.16em] text-[#9A968C]">{a.auteur} · {a.source}</p>
                        </div>
                        <button data-testid={`admin-avis-delete-${i}`} aria-label="Supprimer cet avis" onClick={() => removeAvis(a.id)} className="text-[#9A968C] hover:text-[#A33E2B] transition-colors shrink-0">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      {a.reponse && replyDrafts[a.id] === undefined && (
                        <p data-testid={`admin-avis-reponse-${i}`} className="mt-3 border-l-2 border-[#8C7B5D] bg-[#F6F3EB] px-3 py-2 text-xs text-[#4A4D53]">
                          <span className="block mb-1 font-mono text-[9px] uppercase tracking-[0.16em] text-[#8C7B5D]">Réponse publiée</span>
                          {a.reponse}
                        </p>
                      )}
                      {replyDrafts[a.id] === undefined ? (
                        <button
                          type="button"
                          data-testid={`admin-avis-reply-open-${i}`}
                          onClick={() => setReplyDrafts((d) => ({ ...d, [a.id]: a.reponse || "" }))}
                          className="mt-3 inline-flex items-center gap-2 border border-[#D8D4CA] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#4A4D53] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors"
                        >
                          {a.reponse ? "Modifier la réponse" : "Répondre à cet avis"}
                        </button>
                      ) : (
                        <div className="mt-3 space-y-2">
                          <textarea
                            data-testid={`admin-avis-reply-input-${i}`}
                            className="bm-input min-h-[70px] resize-y"
                            placeholder="Votre réponse, affichée publiquement sous l'avis…"
                            value={replyDrafts[a.id]}
                            onChange={(e) => setReplyDrafts((d) => ({ ...d, [a.id]: e.target.value }))}
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              data-testid={`admin-avis-reply-ai-${i}`}
                              disabled={aiBusy !== ""}
                              onClick={() => assist("avis_reply", `Avis de ${a.auteur} (${a.note}/5) : ${a.texte}`, (t) => setReplyDrafts((d) => ({ ...d, [a.id]: t })))}
                              className="inline-flex items-center gap-2 border border-[#D8D4CA] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#4A4D53] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors disabled:opacity-50"
                            >
                              <Sparkles className="h-3.5 w-3.5" /> {aiBusy === "avis_reply" ? "Génération…" : "Proposition IA"}
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-avis-reply-save-${i}`}
                              onClick={() => saveReponse(a.id)}
                              className="inline-flex items-center gap-2 bg-[#121212] text-[#F6F3EB] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors"
                            >
                              <Check className="h-3.5 w-3.5" /> Publier la réponse
                            </button>
                            <button
                              type="button"
                              data-testid={`admin-avis-reply-cancel-${i}`}
                              onClick={() => setReplyDrafts((d) => { const c = { ...d }; delete c[a.id]; return c; })}
                              className="inline-flex items-center gap-2 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#9A968C] hover:text-[#A33E2B] transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-8 bg-white border border-[#D8D4CA] p-6 sm:p-8" data-testid="admin-settings-section">
              <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#121212] flex items-center gap-3">
                <Save className="h-5 w-5 text-[#8C7B5D]" /> Réglages du site
              </h2>
              <form onSubmit={saveSettings} data-testid="admin-settings-form" className="mt-6 space-y-5">
                <div>
                  <label htmlFor="set-google" className="bm-label">Lien « Laisser un avis Google »</label>
                  <input id="set-google" data-testid="admin-settings-google" className="bm-input" placeholder="https://g.page/… (lien de votre fiche Google Business)" value={settings.google_review_url} onChange={(e) => setSettings((s) => ({ ...s, google_review_url: e.target.value }))} />
                  <p className="mt-1.5 text-xs text-[#9A968C]">Quand votre fiche Google sera finalisée, collez son lien ici : le bouton d'avis pointera directement dessus.</p>
                </div>
                <div>
                  <label htmlFor="set-video" className="bm-label">Vidéo de l'atelier (lien YouTube)</label>
                  <input id="set-video" data-testid="admin-settings-video" className="bm-input" placeholder="https://www.youtube.com/watch?v=… ou https://youtu.be/…" value={settings.video_url} onChange={(e) => setSettings((s) => ({ ...s, video_url: e.target.value }))} />
                  <p className="mt-1.5 text-xs text-[#9A968C]">Téléversez votre vidéo sur YouTube (non répertoriée si vous préférez), puis collez le lien : elle s'affichera en page d'accueil.</p>
                </div>
                <div>
                  <label htmlFor="set-bio" className="bm-label">Votre présentation personnelle (page À propos)</label>
                  <textarea id="set-bio" data-testid="admin-settings-bio" className="bm-input min-h-[140px] resize-y" placeholder="Parlez de vous : votre parcours, votre passion pour la mécanique, ce qui vous tient à cœur…" value={settings.bio_text} onChange={(e) => setSettings((s) => ({ ...s, bio_text: e.target.value }))} />
                  <p className="mt-1.5 text-xs text-[#9A968C]">Ce texte remplace le texte par défaut de la page À propos. Laissez vide pour revenir au texte par défaut.</p>
                </div>
                <div>
                  <label htmlFor="set-promo" className="bm-label">Offre du moment / promotion (bandeau en page d'accueil)</label>
                  <input id="set-promo" data-testid="admin-settings-promo" className="bm-input" placeholder="Ex. Check hiver : batterie, pneus, niveaux, éclairage — CHF …" value={settings.promo_text} onChange={(e) => setSettings((s) => ({ ...s, promo_text: e.target.value }))} />
                  <button
                    type="button"
                    data-testid="admin-ai-promo-btn"
                    disabled={aiBusy !== ""}
                    onClick={() => assist("promo", settings.promo_text ? `Texte actuel à améliorer : ${settings.promo_text}` : "", (t) => setSettings((s) => ({ ...s, promo_text: t })))}
                    className="mt-2 inline-flex items-center gap-2 border border-[#D8D4CA] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[#4A4D53] hover:border-[#8C7B5D] hover:text-[#8C7B5D] transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="h-3.5 w-3.5" /> {aiBusy === "promo" ? "Génération…" : "Générer un texte (IA)"}
                  </button>
                  <p className="mt-1.5 text-xs text-[#9A968C]">Affiche un bandeau en haut de la page d'accueil. Laissez vide pour le masquer — idéal pour vos offres saisonnières (check hiver, promo detailing…).</p>
                </div>
                <button type="submit" data-testid="admin-settings-save" disabled={busy} className="w-full sm:w-auto bg-[#121212] text-[#F6F3EB] font-mono text-xs uppercase tracking-[0.18em] px-6 py-4 hover:bg-[#8C7B5D] hover:text-[#121212] transition-colors disabled:opacity-50">
                  Enregistrer les réglages
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
